import React, { useMemo, useState } from 'react';
import c from 'classnames';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DateRange, type RangeKeyDict, type Range } from 'react-date-range';

import './style.scss';

const DATE_RANGE_COLOR_CODE = '#DE590D';

interface DateRangePickerProps {
  label: string,
  selectionRange: [Range],
  setSelectionRange: (rangesByKey: RangeKeyDict) => void,
  quickSelectMonths?: number,
  placeholder?: string,
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  selectionRange,
  setSelectionRange,
  quickSelectMonths = 0,
  placeholder,
}) => {
  const { t } = useTranslation();

  const [openDatePicker, setOpenDatePicker] = useState(false);

  const displayDate = useMemo(() => (selectionRange[0].startDate && selectionRange[0].endDate ?
    `${format(selectionRange[0].startDate, 'dd/MM/yyyy')} - ${format(selectionRange[0].endDate, 'dd/MM/yyyy')}` :
    (placeholder || t('filters.select_date_range'))
  ), [placeholder, selectionRange, t]);

  const months = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    return Array.from({ length: quickSelectMonths }, (_, n) => {
      // If the month is higher than 12 it will increase the year.
      const startDate = new Date(year, month + n, 1, 0, 0, 0);
      // Day 0 off the next month translates back into the last day of the current month.
      const endDate = new Date(year, month + n + 1, 0, 23, 59, 59);

      return { startDate, endDate, key: 'selection' };
    });
  }, [quickSelectMonths]);

  return (
    <div className="DatePicker">
      <div className="DatePicker__label">{label}</div>
      <div className="DatePicker__wrapper">

        {months.length && months.map((selection, n) => (
          <button
            type="button"
            className={c(
              'DatePicker__quick_select Button',
              selectionRange?.[0]?.startDate?.getTime() === selection.startDate.getTime() &&
              selectionRange?.[0]?.endDate?.getTime() === selection.endDate.getTime() ?
                'Button--primary' : 'Button--inverted',
            )}
            onClick={() => setSelectionRange({ selection })}
          >
            {n > 0 ? format(selection.startDate, 'LLLL') : t('general.current_month')}
          </button>
        ))}
        <div className="DatePicker__select_wrapper">
          <button
            type="button"
            className={c('DatePicker__select')}
            onClick={() => setOpenDatePicker(true)}
          >
            <div className="placeholder">{displayDate}</div>
            <div className="icon icon-calendar icon-variant-0" />
          </button>
          <button
            type="button"
            className={c('DatePicker__backdrop', { hidden: !openDatePicker })}
            onClick={() => setOpenDatePicker(false)}
          >
            {t('button.close')}
          </button>
          <div className={c('DatePicker__picker', { hidden: !openDatePicker })}>
            <DateRange
              ranges={selectionRange}
              onChange={setSelectionRange}
              rangeColors={[DATE_RANGE_COLOR_CODE]}
              color={DATE_RANGE_COLOR_CODE}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
